// Centralized Meta Pixel (browser) + Conversions API (server) helper.
// The CAPI access token lives ONLY server-side; this module never touches it.
// No Pixel is loaded and no event fires before marketing consent.

export const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const CONSENT_KEY = "cm_marketing_consent"; // "yes" | "no" | null

let pixelReady = false;
let lastPageViewPath = null;
let lastViewContentKey = null;
let contactBound = false;

// ---- consent ----
export function getConsent() {
  try { return localStorage.getItem(CONSENT_KEY); } catch (e) { return null; }
}
export function hasConsent() { return getConsent() === "yes"; }
export function setConsent(accepted) {
  try { localStorage.setItem(CONSENT_KEY, accepted ? "yes" : "no"); } catch (e) { /* ignore */ }
}

// ---- ids / cookies ----
export function newEventId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `e-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
export function readCookie(name) {
  if (typeof document === "undefined") return null;
  const row = document.cookie.split("; ").find((x) => x.startsWith(`${name}=`));
  return row ? decodeURIComponent(row.split("=").slice(1).join("=")) : null;
}
export const browserIds = () => ({ fbp: readCookie("_fbp"), fbc: readCookie("_fbc") });

// ---- pixel lifecycle ----
export function enableMetaPixel() {
  if (pixelReady || !PIXEL_ID || typeof window === "undefined") return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("consent", "grant");
  window.fbq("init", PIXEL_ID);
  pixelReady = true;
  pageView(); // initial PageView for the current route
}

export function revokeMetaPixel() {
  if (typeof window !== "undefined" && window.fbq) window.fbq("consent", "revoke");
}

function fbqTrack(name, params, eventID) {
  if (!pixelReady || typeof window === "undefined" || !window.fbq) return false;
  const opts = eventID ? { eventID } : undefined;
  window.fbq("track", name, params || {}, opts);
  return true;
}

// ---- standard events ----
export function pageView() {
  if (!pixelReady) return;
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (path === lastPageViewPath) return; // guard re-render / double init
  lastPageViewPath = path;
  fbqTrack("PageView");
}

export function viewContent({ contentIds, contentName, value, currency = "EUR" } = {}) {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const key = `${path}|${(contentIds || []).join(",")}`;
  if (key === lastViewContentKey) return; // guard re-render / StrictMode double-fire
  lastViewContentKey = key;
  const params = { content_type: "product" };
  if (contentIds && contentIds.length) params.content_ids = contentIds;
  if (contentName) params.content_name = contentName;
  if (value && value > 0) { params.value = value; params.currency = currency; }
  fbqTrack("ViewContent", params, newEventId());
}

// eventId must equal the server-side event_id (submission_id) for dedup.
export function lead({ eventId, leadType, contentIds, contentName, value, currency = "EUR" } = {}) {
  const params = {};
  if (leadType) params.lead_type = leadType;
  if (contentIds && contentIds.length) params.content_ids = contentIds;
  if (contentName) params.content_name = contentName;
  if (value && value > 0) { params.value = value; params.currency = currency; }
  fbqTrack("Lead", params, eventId || newEventId());
}

export function contact(method) {
  fbqTrack("Contact", { contact_method: method }, newEventId());
}

// Single delegated listener so we don't scatter fbq() calls across components.
export function initContactTracking() {
  if (contactBound || typeof document === "undefined") return;
  contactBound = true;
  document.addEventListener("click", (e) => {
    const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (!a) return;
    const href = a.getAttribute("href") || "";
    if (href.startsWith("tel:")) contact("phone");
    else if (href.includes("wa.me") || href.includes("whatsapp.com")) contact("whatsapp");
  }, true);
}
